import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router';

import { useSession } from '@/auth/session';
import { desktopSync } from '@/desktop/bridge';

import { decideAccess } from '../access';
import { paths } from '../paths';
import { queuedWritesAccess } from './access';

/**
 * How the back office finds out that a till has writes it could not deliver.
 *
 * A quarantined write is a sale, a movement or a close that the server refused
 * for good. Nothing retries it and nothing else announces it, so without a
 * count somewhere the failure mode is silence: the till keeps working, the day
 * looks normal, and the gap is found at cash-up or not at all.
 *
 * It renders nothing at all when there is nothing to say — in a browser, which
 * has no queue, and on a terminal whose queue is clear. A permanent chip reading
 * "0 refused" is furniture, and furniture is what people stop seeing.
 *
 * Re-read on every navigation rather than on a timer. The count only changes
 * when a drain refuses something, a person looking at the back office is
 * navigating, and a poll running behind every screen for a number that is
 * usually zero is not worth the wake-ups on a machine that is also a till.
 *
 * Drawn only for somebody the screen behind it would admit, and decided by
 * `decideAccess` against that screen's own declaration rather than by a role
 * test written here. That is the rule `navigation.ts` applies to every route
 * with a `nav` entry — a link appears exactly when the route would render — and
 * this route has none, so it says it itself. A second opinion about who may open
 * the screen is precisely how somebody is shown a control that refuses them.
 */
export function RefusedWritesLink() {
  const [refused, setRefused] = useState(0);
  const { pathname } = useLocation();
  const { session } = useSession();

  const admitted =
    decideAccess(
      queuedWritesAccess,
      session === null
        ? null
        : {
            role: session.user.role,
            mustChangeCredential: session.user.mustChangeCredential,
          },
    ) === 'render';

  useEffect(() => {
    // Not asked for at all by somebody who could not open the answer. The count
    // is only a number, but the cheapest way not to leak one is not to fetch it.
    if (!admitted) {
      return;
    }

    const sync = desktopSync();

    if (sync === null) {
      return;
    }

    let live = true;

    sync
      .status()
      .then((counts) => {
        if (live) setRefused(counts.blocked);
      })
      .catch((error: unknown) => {
        // Nothing on screen. This is a courtesy count beside the sign-out
        // button, and an alarm about a failed IPC call would be a worse thing to
        // put in front of somebody than the missing number.
        console.error('sync: the terminal’s queue could not be read', error);
      });

    return () => {
      live = false;
    };
  }, [admitted, pathname]);

  // Belt to the effect's braces. A count already in state when a role changes
  // under the same mounted shell — a second tab signing in as somebody else,
  // since the session lives in `sessionStorage` — would otherwise stay drawn.
  if (!admitted || refused === 0) {
    return null;
  }

  return (
    <Link
      to={paths.queuedWrites}
      // `--destructive` is the token for refusal and this is one, tinted rather
      // than filled — the shape the destructive button already uses. A filled
      // pill here would read as the screen's primary action, and the design
      // language allows one accent per screen.
      //
      // It is still the only tinted thing in the rail: the active navigation
      // item used to be one too, and was changed to a neutral mark precisely so
      // that a colour in this column means something has gone wrong rather than
      // meaning "you are here".
      //
      // **The rail went slate and the lettering had to follow.** `text-
      // destructive` reads on the enamel margin this replaced; on slate it
      // measures 3.46:1 and misses AA at this size. The tint carries the
      // meaning and `--destructive-foreground` carries the words — 13.78:1 on
      // the same pill — which is the treatment the rail's active item uses and
      // costs no new value. This renders on a terminal and never in a browser,
      // so it is the one thing in this file that a back-office render cannot
      // show; it was measured rather than looked at.
      className="rounded-4xl bg-destructive/25 px-2.5 py-1.5 text-sm text-destructive-foreground"
    >
      <span className="numeric">{refused}</span>{' '}
      {refused === 1 ? 'write refused' : 'writes refused'}
    </Link>
  );
}
